import React, { useState, useEffect } from 'react';
import { Phone, Facebook, Instagram, Menu, X } from 'lucide-react';
import { businessInfo } from '../mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    { label: 'Home', id: 'home' },
    { label: 'About Us', id: 'about' },
    { label: 'Our Work', id: 'gallery' },
    { label: 'Testimonials', id: 'reviews' },
    { label: 'Contact Us', id: 'contact' }
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ backgroundColor: '#1c2536' }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12">
        {/* Top Row: Logo, Phone, Socials, Menu */}
        <div className="flex items-center justify-between h-16 sm:h-18 md:h-20 border-b border-white/10">
          {/* Logo - Left on mobile, Center on desktop */}
          <div className="lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
            <button
              onClick={() => scrollToSection('home')}
              className="flex items-center group"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_1fb2ae2e-514b-4c98-bafe-f224b06bbc59/artifacts/uzfyvo1s_logo%20new.jpg"
                alt="JP Joinery"
                className="h-10 sm:h-14 md:h-16 lg:h-20 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </button>
          </div>

          {/* Phone (desktop only - left side) */}
          <a
            href={`tel:${businessInfo.phone}`}
            className="hidden lg:flex items-center space-x-2 text-white hover:text-yellow-400 font-semibold transition-colors group"
          >
            <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            <span className="text-base">{businessInfo.phone}</span>
          </a>

          {/* Right side: Phone (mobile), Socials, Menu */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            {/* Phone icon - mobile only */}
            <a
              href={`tel:${businessInfo.phone}`}
              className="lg:hidden flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-amber-400 text-white hover:text-gray-900 transition-all duration-300"
              aria-label="Call us"
            >
              <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </a>
            
            {/* Social icons */}
            <a
              href="https://www.facebook.com/p/JPJoinery-100091386755192/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white hover:text-yellow-400 transition-all duration-300 hover:scale-110"
              aria-label="Facebook"
            >
              <Facebook className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            </a>
            <a
              href="https://www.instagram.com/jp.joinerycarpentry/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 text-white hover:text-yellow-400 transition-all duration-300 hover:scale-110"
              aria-label="Instagram"
            >
              <Instagram className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            </a>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              className="lg:hidden flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-yellow-400 hover:bg-yellow-300 text-gray-900 rounded-full shadow-md transition-all duration-300 hover:scale-105"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Menu className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
            </button>
          </div>
        </div>

        {/* Bottom Row: Navigation Menu - Desktop */}
        <div className="hidden lg:flex items-center justify-center h-12">
          <nav className="flex items-center gap-10">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className="text-white/90 hover:text-yellow-400 font-medium text-sm uppercase tracking-wide transition-colors duration-200 relative group whitespace-nowrap"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </nav>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full shadow-lg border-t border-white/10 z-50" style={{ backgroundColor: '#1c2536' }}>
            <nav className="flex flex-col py-2">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-white/90 hover:text-yellow-400 hover:bg-white/5 font-medium text-sm sm:text-base py-3 px-4 sm:px-6 text-left transition-all duration-200"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
